/**
 * @fileoverview OptionLoader class for loading and managing select options from
 * API endpoints. Uses lazy loading to prevent side effects at module import time.
 */

import { apiClient } from './api-client';
import { EntityType, getCache } from './cache-registry';
import { SelectOption } from './types';
import { sessionAuth } from './session';

interface OptionItem {
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Configuration for creating an OptionLoader.
 */
interface OptionLoaderConfig<T extends OptionItem> {
  endpoint: string;
  labelField: keyof T;
  valueField: keyof T;
  noValueLabel: string;
}

export class OptionLoader<T extends OptionItem = OptionItem> {
  private options: SelectOption[];
  private endpoint: string;
  private labelField: keyof T;
  private valueField: keyof T;
  private noValueLabel: string;
  private loading: boolean;
  private loaded: boolean;
  private loadPromise: Promise<void> | null;

  constructor(config: OptionLoaderConfig<T>) {
    this.endpoint = config.endpoint;
    this.labelField = config.labelField;
    this.valueField = config.valueField;
    this.noValueLabel = config.noValueLabel;
    this.options = [{ value: '', label: this.noValueLabel }];
    this.loading = false;
    this.loaded = false;
    this.loadPromise = null;
  }

  /**
   * Ensures options are loaded. Call this before accessing options.
   * Safe to call multiple times - will only load once.
   */
  async ensureLoaded(): Promise<void> {
    if (this.loaded) {
      return;
    }
    
    if (this.loadPromise) {
      return this.loadPromise;
    }
    
    // Check for auth before loading
    if (!sessionAuth.currentUser) {
      // Wait for auth to be ready
      await this.waitForAuth();
    }
    
    this.loadPromise = this.loadOptions();
    return this.loadPromise;
  }

  /**
   * Waits for session auth to be ready.
   */
  private waitForAuth(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (sessionAuth.currentUser) {
        resolve();
        return;
      }
      
      const unsubscribe = sessionAuth.onAuthStateChanged((user) => {
        if (user) {
          unsubscribe();
          resolve();
        }
      });
      
      // Timeout after 10 seconds
      setTimeout(() => {
        unsubscribe();
        resolve();
      }, 10000);
    });
  }

  /**
   * Loads options from the API.
   */
  private async loadOptions(): Promise<void> {
    if (this.loading || this.loaded) {
      return;
    }
    
    this.loading = true;
    
    try {
      const data = await apiClient.getMany<T>(this.endpoint, {
        page: '0',
        pageSize: '1000'
      });

      // Clear existing data but keep the "no value" option
      this.options.splice(1);
      this.options.push(...data.items.map((item: T) => ({
        value: String(item[this.valueField] ?? ''),
        label: String(item[this.labelField] ?? ''),
      })));

      // Clear the cache to force refresh of dependent data
      const cache = getCache(this.endpoint as EntityType);
      cache.clear();
      
      this.loaded = true;
    } catch (error) {
      console.error(`Error fetching ${this.endpoint} options:`, error);
    } finally {
      this.loading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Forces a reload of options from the API.
   */
  async reload(): Promise<void> {
    this.loaded = false;
    this.loadPromise = null;
    return this.ensureLoaded();
  }

  /**
   * Gets options synchronously. Returns cached options or placeholder if not loaded.
   * For guaranteed data, use ensureLoaded() first.
   */
  getOptions(): SelectOption[] {
    // Trigger lazy load in background if not loaded
    if (!this.loaded && !this.loading) {
      void this.ensureLoaded();
    }
    return this.options;
  }

  /**
   * Gets option by value.
   */
  getOptionByValue(value: string): SelectOption | undefined {
    return this.options.find(option => option.value === value);
  }

  /**
   * Gets option by label.
   */
  getOptionByLabel(label: string): SelectOption | undefined {
    return this.options.find(option => option.label === label);
  }

  /**
   * Gets label by value.
   */
  getLabelByValue(value: string): string {
    const option = this.getOptionByValue(value);
    return option ? option.label : value || '';
  }

  /**
   * Gets value by label.
   */
  getValueByLabel(label: string): string {
    const option = this.getOptionByLabel(label);
    return option ? option.value : '';
  }

  /**
   * Returns whether options have been loaded.
   */
  isLoaded(): boolean {
    return this.loaded;
  }
}

/**
 * Factory function to create option loaders with proper configuration.
 */
export function createOptionLoader<T extends OptionItem = OptionItem>(
  endpoint: string,
  labelField: keyof T = 'name' as keyof T,
  valueField: keyof T = 'id' as keyof T,
  noValueLabel?: string
): OptionLoader<T> {
  return new OptionLoader<T>({
    endpoint,
    labelField,
    valueField,
    noValueLabel: noValueLabel ?? `(No ${endpoint.charAt(0).toUpperCase() + endpoint.slice(1)})`,
  });
}
